import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from '../cx.js';

export type ButtonVariant = 'primary' | 'secondary' | 'text';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 主（一屏一个）/ 次 / 文字（PRD §3.1.2「一屏一个主操作，其余降为次要按钮或文字链接」） */
  variant?: ButtonVariant;
  /** 撑满容器宽度 */
  block?: boolean;
  /** 处理中：禁用并标记 aria-busy，文字保持不变 */
  loading?: boolean;
}

const BASE =
  'inline-flex min-h-touch min-w-touch max-w-full shrink-0 select-none items-center justify-center gap-2 rounded-xl text-base font-semibold leading-snug ' +
  'transition-colors duration-micro focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand px-5 text-on-brand hover:bg-brand-hover active:bg-brand-active',
  secondary: 'border border-border-strong bg-surface px-5 text-fg hover:bg-surface-2',
  text: 'bg-transparent px-3 text-brand-fg hover:underline',
};

/** 按钮：所有尺寸的可点区域都 ≥44 × 44（PRD §8.7）。文字由调用方传入。 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', block = false, loading = false, disabled, className, type = 'button', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-variant={variant}
      className={cx(BASE, VARIANTS[variant], block && 'w-full', className)}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      )}
      {children}
    </button>
  );
});
